/**
 * Strong CONCORDANCE panel (reader feature).
 *
 * When selecting a tagged word in a verse, this panel resolves its Strong
 * entry OFFLINE-FIRST:
 *   1. Local PowerSync base (`strong_entry`, seeded at startup) → structured
 *      entry: number, original lemma, transliteration, gloss and definition.
 *   2. Native Tauri command (`get_definition_native`) when running in Tauri.
 *   3. Rust server HTTP API as a last resort (codes outside the
 *      local seed).
 *
 * This is a self-contained component: the container only passes it the selected
 * Strong code, the drizzle client, and the close/navigation callbacks between
 * cross-references.
 */

import { createEffect, createResource, createSignal, Show, Suspense } from "solid-js";
import { Info, RefreshCw, X } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { api } from "@/lib/api";
import { handleDefinitionLinkClick } from "@/utils/dictionary-links";
import type { PowerSyncDrizzleDB } from "@bso/powersync";
import type { components } from "@bso/client";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { SHEET_PULL_DISMISS } from "@/lib/gestures/presets";
import { useBackDismiss } from "@/lib/use-back-dismiss";
import { loadStrongEntryFromLocal, type StrongEntryView } from "../queries/load-strong-entry";

type DictionaryEntryResponse = components["schemas"]["DictionaryEntryResponse"];

interface StrongConcordanceResult {
  /** Structured entry (local seed). `null` if it came from the server/Tauri. */
  local: StrongEntryView | null;
  /** Definition HTML (Tauri/HTTP) when there is no local entry. */
  html: DictionaryEntryResponse | null;
}

interface StrongConcordancePanelProps {
  /** Selected Strong code (e.g. "H7225", "G3056"). */
  code: string;
  /** drizzle/PowerSync client for offline resolution. */
  db: PowerSyncDrizzleDB | null;
  /** Closes the panel. */
  onClose: () => void;
  /** Navigates to another code (cross-references in the definition HTML). */
  onSelectCode: (code: string) => void;
}

async function resolveStrong(
  db: PowerSyncDrizzleDB | null,
  code: string,
): Promise<StrongConcordanceResult> {
  // 1) Local base (offline-first).
  const local = await loadStrongEntryFromLocal(db, code);
  if (local) {
    return { local, html: null };
  }

  // 2) Native Tauri command (offline on desktop/mobile, outside the seed).
  if (typeof globalThis !== "undefined" && "__TAURI_INTERNALS__" in globalThis) {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const nativeDef = await invoke<DictionaryEntryResponse | null>("get_definition_native", {
        word: code,
      });
      if (nativeDef) {
        return { local: null, html: nativeDef };
      }
    } catch (e) {
      console.warn("Failed to get definition natively, falling back to HTTP api", e);
    }
  }

  // 3) Rust server HTTP API (last resort).
  try {
    const { data, response } = await api.GET("/api/v1/bible/dictionary/definition/{word}", {
      params: { path: { word: code } },
    });
    if (response.ok && data) {
      return { local: null, html: data };
    }
  } catch (e) {
    console.warn("[reader] Could not fetch Strong definition (offline?)", e);
  }

  return { local: null, html: null };
}

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
// Below `lg` the panel is a bottom sheet; at/above it becomes a static side
// panel where pull-to-close must stay inert. Matches the Tailwind `lg` breakpoint.
const DESKTOP_PANEL_QUERY = "(min-width: 1024px)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

function isDesktopPanel(): boolean {
  return globalThis.matchMedia?.(DESKTOP_PANEL_QUERY).matches ?? false;
}

export function StrongConcordancePanel(props: StrongConcordancePanelProps) {
  const { t } = useI18n();

  // This panel is only mounted while open (parent gates it with <Show>), so the
  // open signal is constant true; browser "back" closes it instead of navigating.
  useBackDismiss(
    () => true,
    () => {
      props.onClose();
    },
  );

  const [entry] = createResource(
    () => props.code,
    // The fetcher runs within the scope tracked by its source (props.code);
    // reading props.db here is not lost reactivity. False positive from solid/reactivity.
    // oxlint-disable-next-line solid/reactivity
    (code) => resolveStrong(props.db, code),
  );

  // --- Pull-to-close (mobile bottom sheet) --------------------------------
  // Dragging the sheet downward past a commit threshold closes it, alongside
  // Escape and the close button. Shared recognizer = same physics as the rest of
  // the app. `canStart` ignores drags that begin on the scrollable definition
  // (unless it is already scrolled to the top) so reading the entry never fights
  // the dismiss. Desktop renders as a side panel where this gesture is inert
  // (a downward drag there simply rubber-bands and snaps back).
  const [sheetY, setSheetY] = createSignal(0);
  const [sheetSettling, setSheetSettling] = createSignal(false);
  let scrollerRef: HTMLDivElement | undefined;
  let closing = false;

  const closeRecognizer = createGestureRecognizer({
    axis: "y",
    thresholdPx: SHEET_PULL_DISMISS.thresholdPx,
    fling: { ...SHEET_PULL_DISMISS.fling },
    canStart: ({ target }) => {
      // Desktop renders a static side panel — pull-to-close is mobile-only.
      if (isDesktopPanel()) {
        return false;
      }
      // Allow the drag unless it starts inside the content scroller while that
      // scroller is mid-scroll (then the finger should scroll, not dismiss).
      if (scrollerRef && target instanceof Node && scrollerRef.contains(target)) {
        return scrollerRef.scrollTop <= 0;
      }
      return true;
    },
    canEngage: (axis, sample) => axis === "y" && sample.dy > 0,
    onStart: () => {
      closing = false;
      setSheetSettling(false);
    },
    onMove: (sample) => {
      // The sheet is being dragged off-screen, so it tracks the finger 1:1
      // downward; an upward over-pull is damped so it feels tethered, never
      // detaching from the bottom edge.
      setSheetSettling(false);
      setSheetY(sample.dy >= 0 ? sample.dy : sample.dy * SHEET_PULL_DISMISS.upwardFactor);
    },
    onEnd: (sample) => {
      closing = sample.dy >= SHEET_PULL_DISMISS.commitThresholdPx || sample.isFling;
      settleSheet();
    },
    onCancel: settleSheet,
  });

  function settleSheet() {
    setSheetSettling(true);
    setSheetY(0);
    if (closing) {
      closing = false;
      vibrate(SHEET_PULL_DISMISS.hapticMs);
      props.onClose();
    }
  }

  const sheetHandlers = pointerHandlers(closeRecognizer);

  return (
    <>
      <button
        type="button"
        class="fixed inset-0 bg-black/50 backdrop-blur-xs z-30 lg:hidden cursor-default w-full h-full border-none p-0 outline-none"
        onClick={() => props.onClose()}
      />

      {/* Slide-up bottom sheet on mobile, fixed side panel on desktop.
          On mobile the sheet supports pull-to-close: dragging it down past a
          threshold dismisses it. `touch-action: pan-y` lets the recognizer claim
          the vertical drag while native scroll still works inside the content. */}
      <div
        data-testid="strong-panel"
        class="fixed bottom-0 left-0 right-0 max-h-[50dvh] bg-card border-t border-border/50 rounded-t-[32px] shadow-2xl flex flex-col z-40
               lg:relative lg:inset-y-0 lg:right-0 lg:left-auto lg:bottom-auto lg:w-96 lg:max-h-none lg:h-full lg:rounded-none lg:border-t-0 lg:border-l lg:border-border/40 lg:z-10 lg:shadow-none"
        style={{
          transform: `translateY(${sheetY()}px)`,
          transition:
            sheetSettling() && !prefersReducedMotion()
              ? `transform ${SHEET_PULL_DISMISS.transitionMs}ms cubic-bezier(0.19, 1, 0.22, 1)`
              : "none",
          "touch-action": "pan-y",
        }}
        {...sheetHandlers}
      >
        <div class="w-12 h-1.5 rounded-full bg-muted-foreground/30 mx-auto my-3 shrink-0 lg:hidden" />

        {/* Header */}
        <div class="flex items-center justify-between px-6 pb-4 pt-1 lg:pt-4 border-b border-border/30 shrink-0">
          <div class="flex items-center gap-2 font-bold text-sm text-primary font-mono">
            <Info class="w-4 h-4 text-primary" />
            <span data-testid="strong-code">
              {t(msg`Strong's Concordance`)}: {props.code}
            </span>
          </div>
          <button
            type="button"
            onClick={() => props.onClose()}
            class="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t(msg`Close`)}
          >
            <X class="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div ref={scrollerRef} class="flex-1 overflow-y-auto p-6 space-y-4">
          <Suspense
            fallback={
              <div class="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground animate-pulse">
                <RefreshCw class="w-4 h-4 animate-spin" />
                <span>{t(msg`Loading definition...`)}</span>
              </div>
            }
          >
            <Show
              when={entry()?.local}
              fallback={
                <Show
                  when={entry()?.html}
                  fallback={
                    <div class="text-sm text-red-500 py-4" data-testid="strong-not-found">
                      {(() => {
                        const codeVal = props.code;
                        return t(msg`No definition found for code ${codeVal}.`);
                      })()}
                    </div>
                  }
                >
                  {(def) => (
                    <div class="space-y-3">
                      <h2 class="text-xl font-bold text-foreground font-mono">{def().word}</h2>
                      {/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- delegation to internal keyboard-accessible anchors */}
                      <div
                        ref={(el) => {
                          createEffect(() => {
                            el.innerHTML = def().definition;
                          });
                        }}
                        onClick={(e) => {
                          handleDefinitionLinkClick(e, props.onSelectCode);
                        }}
                        class="text-[0.95rem] leading-[1.75] text-foreground/85 bg-muted/15 p-5 rounded-2xl border border-border/30 prose dark:prose-invert max-w-none font-serif
                               prose-p:my-2 prose-strong:text-primary prose-strong:font-bold [&_a]:text-accent [&_a]:font-semibold [&_a]:underline [&_a]:decoration-accent/40"
                      />
                    </div>
                  )}
                </Show>
              }
            >
              {(e) => (
                <div class="space-y-4" data-testid="strong-entry">
                  {/* Lema original + transliteración */}
                  <div class="space-y-1">
                    <h2
                      class="text-3xl font-bold text-foreground"
                      lang={e().language === "hebrew" ? "he" : "el"}
                      dir={e().language === "hebrew" ? "rtl" : "ltr"}
                    >
                      {e().lemma}
                    </h2>
                    <div class="flex items-baseline gap-2 flex-wrap">
                      <span
                        class="text-base font-semibold text-primary"
                        data-testid="strong-translit"
                      >
                        {e().transliteration}
                      </span>
                      <Show when={e().pronunciation}>
                        <span class="text-sm text-muted-foreground font-mono">
                          ({e().pronunciation})
                        </span>
                      </Show>
                    </div>
                  </div>

                  {/* Glosa breve */}
                  <Show when={e().gloss}>
                    <p class="text-sm font-semibold text-foreground/90" data-testid="strong-gloss">
                      {e().gloss}
                    </p>
                  </Show>

                  {/* Definición */}
                  <Show when={e().definition}>
                    <div class="text-[0.95rem] leading-[1.75] text-foreground/85 bg-muted/15 p-5 rounded-2xl border border-border/30 font-serif">
                      {e().definition}
                    </div>
                  </Show>

                  {/* Etiqueta de idioma original */}
                  <p class="text-2xs uppercase tracking-wider font-bold text-muted-foreground/70">
                    {e().language === "hebrew"
                      ? t(msg`Hebrew (Old Testament)`)
                      : t(msg`Greek (New Testament)`)}
                  </p>
                </div>
              )}
            </Show>
          </Suspense>
        </div>
      </div>
    </>
  );
}
